import Document from "documents/document";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import { styled, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import DocumentRedlineSelection from "work/proposals/redlining/field-redlines/_views/document-redline-selection";
import { FieldRedlineArray } from "work/proposals/redlining/field-redlines/field-redline";
import ProposalRedline from "work/proposals/redlining/proposal-redline";
import downloadFile from "_shared/_utilities/download-file";
import Guid from "_shared/guids/guid";
import { useLazyDownloadDocumentQuery } from "documents/_api/document-api";
import { showErrorSnackbar } from "_shared/_errors/error-snackbar";
import DocumentSelector from "documents/_views/selectors/document-selector";
import DocumentUploadOverlay from "documents/_views/uploader/document-upload-overlay";
import UnavailableDocument from "documents/unavailable-documents/unavailable-document";
import React from "react";
import { useSession } from "users/__session/session-context";
import { AccountType } from "users/account-types/account-type";
import ProposalIssueList from "work/proposals/issues/_views/proposal-issue-list";
import ProposalIssuesBadge from "work/proposals/issues/_views/proposal-issues-badge";
import type { ProposalUnreadEnvelope } from "work/proposals/negotiation/proposal-field-unread";
import { ProposalFieldCategory } from "work/proposals/proposal-fields/proposal-field";
import ProposalIssues from "work/proposals/issues/proposal-issues";
import WorkDocument from "work/work-agreements/contracts/work-document";
import { WorkDocumentType } from "work/work-agreements/contracts/work-document-type";
import { Masked } from "work/masked-fields";
import ResizableTemplatePanel from "work/proposals/_views/_shared/resizable-template-panel";
import getReplacementDocumentTemplateIds from "work/proposals/_views/active/tabs/replacement-document-template-ids";
import { MaskedProposalField, maskedProposalFieldNotice } from "work/proposals/_views/_shared/masked-field-notice";
import {
  paneIsCompact,
  workPaneProseInset,
} from "work/_views/work-pane-layout";
import { useWorkPaneIsCompact } from "work/_views/use-work-pane-size";

const DOCUMENT_ROW_MAX_WIDTH = "550px";

/**
 * Which of the two collections the tab shows.
 *
 * One at a time, behind a strip, at every width (#6035). Phones switched first (#5907): stacked, each
 * table was a strip of its own rows between a heading and the next table's toolbar. Wider panes drew
 * both, and two headings, two toolbars and two table headers were more than the tab needed to say.
 * Each tab carries its collection's count and badge, so the one out of sight still says what it holds.
 */
type PolicyCollection = "client" | "vendor";

/** The collection a field belongs to, for following the review traverser onto the other one. */
function policyCollectionOf(field: ProposalField | undefined): PolicyCollection | undefined {
  if (field?.name === ProposalField.ClientPolicies.name) return "client";
  if (field?.name === ProposalField.VendorPolicies.name) return "vendor";
  return undefined;
}

/**
 * The strip, on the prose inset's line and as short as a tab strip can be, with the rule under it that
 * the collection headings used to draw. Spaced like the page's own tab strip: a gap, not each tab's
 * padding, so the first label starts on the table toolbar's edge. Full width on a phone, as before.
 */
const PolicyCollectionTabs = styled(Tabs)(({ theme }) => ({
  ...workPaneProseInset(theme),
  borderBottom: `1px solid ${theme.palette.divider}`,
  flexShrink: 0,
  // On the templates panel's tab line beside it, which its toolbar sets 8px down.
  marginTop: theme.spacing(1),
  minHeight: 40,
  "& .MuiTabs-list": { gap: theme.spacing(4) },
  "& .MuiTab-root": { minHeight: 40, minWidth: 0, paddingBlock: theme.spacing(0.5), paddingInline: 0 },
  // Compact, the templates panel is a collapsed summary above rather than a column beside.
  [paneIsCompact(theme)]: { marginTop: 0 },
  [theme.breakpoints.down("sm")]: {
    "& .MuiTabs-list": { gap: 0 },
    "& .MuiTab-root": { paddingInline: theme.spacing(2) },
  },
}));

const PolicyCollectionLabel = styled("span")(({ theme }) => ({
  alignItems: "center",
  display: "inline-flex",
  gap: theme.spacing(1),
}));

/** How many documents the collection holds: what the tab out of sight is hiding. */
const PolicyCollectionCount = styled("span")(({ theme }) => ({
  backgroundColor: theme.palette.action.selected,
  borderRadius: theme.spacing(1.25),
  color: theme.palette.text.secondary,
  fontSize: "0.75rem",
  lineHeight: "20px",
  minWidth: 20,
  paddingInline: theme.spacing(0.75),
  textAlign: "center",
}));

// At module scope: each keys its badge's summary, which a fresh function would recompute every render.
const isClientPolicyField = (field: ProposalField) => field.name === ProposalField.ClientPolicies.name;
const isVendorPolicyField = (field: ProposalField) => field.name === ProposalField.VendorPolicies.name;

const POLICY_COLLECTION_ABOUT: Readonly<Record<PolicyCollection, string>> = {
  client: "Client policies establish the terms and conditions that the client requires for the vendor to adhere to",
  vendor: "Vendor policies establish the terms and conditions that the vendor requires for the client to adhere to",
};

/**
 * A strip tab's label: the collection's name with its issue and unseen-change chips, its count, and the
 * explanation the collection heading's info button used to carry, as the label's tooltip.
 * `describeChild`, so the tooltip describes the tab rather than replacing its name.
 */
function PolicyCollectionTabLabel(
  props: Readonly<{
    title: string;
    about: string;
    count: number;
    issues?: ProposalIssues;
    envelope?: ProposalUnreadEnvelope;
    currentUserId?: string;
    fieldFilter: (field: ProposalField) => boolean;
  }>,
) {
  const documents = props.count === 1 ? "document" : "documents";
  return (
    <Tooltip title={props.about} enterDelay={600} placement="bottom-start" describeChild>
      <PolicyCollectionLabel>
        <ProposalIssuesBadge
          title={props.title}
          category={ProposalFieldCategory.Policies}
          issues={props.issues}
          envelope={props.envelope}
          currentUserId={props.currentUserId}
          fieldFilter={props.fieldFilter}
        />
        <PolicyCollectionCount aria-label={`${props.count} ${documents}`}>{props.count}</PolicyCollectionCount>
      </PolicyCollectionLabel>
    </Tooltip>
  );
}

const ListContainer = styled(Grid)(({ theme }) => ({
  flex: 1,
  flexWrap: "nowrap",
  minHeight: "20rem",
  height: "100%",
  width: "100%",
  [paneIsCompact(theme)]: {
    flexDirection: "column",
    flexWrap: "nowrap",
    height: "auto",
    minHeight: 0,
  },
}));
const SelectorDivider = styled(Grid)(({ theme }) => ({
  [paneIsCompact(theme)]: {
    display: "none",
  },
}));
const SelectedDocsList = styled(DocumentRedlineSelection)(({ theme }) => ({
  // No top margin under a narrower pane: it stood the collection's title 16px further off the switch
  // or notice above it than anything else on the tab (#6041).
  flex: 1,
  maxHeight: "60vh",
  // No left margin: the table meets the resize divider, the way the fee schedule's does. The prose
  // above it carries its own inset instead — see `workPaneProseInset`.
  minWidth: 0,
  position: "relative",
  // #4955: the policies are a table now, and it caps its own row width and drops that cap on a narrow
  // screen. What used to be said here about `.MuiListItem-root`, the list's subheader and the inline
  // secondary action has no shape left to address.
  [paneIsCompact(theme)]: {
    boxSizing: "border-box",
    marginLeft: 0,
    maxHeight: "none",
    maxWidth: "none",
    minWidth: 0,
    padding: 0,
    width: "100%",
    "&&": {
      marginLeft: 0,
      maxWidth: "none",
      minWidth: 0,
      padding: 0,
      width: "100%",
    },
  },
}));
const Uploader = styled(DocumentUploadOverlay)(() => ({
  backgroundColor: "rgba(250, 250, 250, 0.5)",
  backdropFilter: "blur(5px) saturate(200%)",
  position: "absolute",
  top: 0,
  left: 0,
  margin: "1rem",
  width: "calc(100% - 2rem)",
  height: "calc(100% - 2rem)",
  zIndex: 9999,
}));
const HeadControls = styled("div")(({ theme }) => ({
  ...workPaneProseInset(theme),
  alignItems: "flex-start",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  maxWidth: DOCUMENT_ROW_MAX_WIDTH,
  width: "100%",
  [paneIsCompact(theme)]: {
    maxWidth: "none",
  },
}));
const SelectedDocsPanel = styled("div")(() => ({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  minWidth: 0,
  width: "100%",
}));
const DocumentGrid = styled("div")(({ theme }) => ({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  minWidth: 0,
  width: "100%",
  [paneIsCompact(theme)]: {
    width: "100%",
  },
}));

type PoliciesTabProps = {
  proposalId?: Guid;
  proposalRedline: ProposalRedline;
  issues?: ProposalIssues;
  /** For each collection tab's unseen-change chip; omitted before the negotiation stream exists. */
  unreadEnvelope?: ProposalUnreadEnvelope;
  currentUserId?: string;
  activeReviewField: ProposalField | undefined;
  negotiationId?: string;
  viewerScope?: string;
  disableEditing?: boolean;
  fieldLockTooltip?: string;
  /** A side pane is taking the row's width, so the templates column gives up its own — see `ResizableTemplatePanel`. */
  isDrawerOpen: boolean;
  onProposalRedlineChange: (newRedline: ProposalRedline, traversalFieldOverride?: ProposalField | null) => void;
  onTraverseToNewField: (fieldInfo: ProposalField | undefined) => void;
};

const PoliciesTab = (props: Readonly<PoliciesTabProps>) => {
  const {
    proposalRedline,
    proposalId,
    activeReviewField,
    negotiationId,
    viewerScope,
    disableEditing,
    isDrawerOpen,
    fieldLockTooltip,
    onProposalRedlineChange,
    onTraverseToNewField,
  } = props;

  // The template selector collapses to its summary when there is no room to hold it open. The pane
  // is what decides that here, not the window — an open comments panel takes a column out of it.
  const selectorIsCompact = useWorkPaneIsCompact();
  const theme = useTheme();
  const phoneQuery = theme.breakpoints.down("sm");
  const isPhone = useMediaQuery(phoneQuery);
  const [isDownloadingFile, setIsDownloadingFile] = React.useState<Guid | null>(null);
  const [documentTypeToUpload, setDocumentTypeToUpload] = React.useState<WorkDocumentType>();
  const [documentIdToReplace, setDocumentIdToReplace] = React.useState<Guid | null>(null);
  const [creatingDocumentFromTemplate, setCreatingDocumentFromTemplate] = React.useState<Guid | null>(null);

  const issues = React.useMemo(
    () =>
      props.issues?.entries.filter((issue) =>
        [
          ProposalField.Policies,
          ProposalField.VendorPolicies,
          ProposalField.ClientPolicies,
          ProposalField.VendorPolicy(issue.field.id ?? Guid.EMPTY),
          ProposalField.ClientPolicy(issue.field.id ?? Guid.EMPTY),
        ].some((candidate) => candidate.isEqualTo(issue.field))
      ),
    [props.issues?.entries]
  );

  const session = useSession();
  const [downloadDocument] = useLazyDownloadDocumentQuery();

  // The collection the review is sitting on, or else the viewer's own — the one they can add to. The
  // page mounts this tab only while it is shown, so a stop chosen from another tab (Next, an issue
  // link) arrives with the tab rather than after it, and has to decide where the strip opens. A stop
  // that moves later switches the strip too, so Prev and Next never land on a table that is not
  // drawn; adjusted while rendering rather than in an effect, so the wrong table is never painted.
  const ownCollection: PolicyCollection = session.context?.viewingAsVendor ? "vendor" : "client";
  const [shownCollection, setShownCollection] = React.useState<PolicyCollection>(
    () => policyCollectionOf(activeReviewField) ?? ownCollection,
  );
  const [followedField, setFollowedField] = React.useState(activeReviewField);
  if (followedField !== activeReviewField) {
    setFollowedField(activeReviewField);
    const reviewedCollection = policyCollectionOf(activeReviewField);
    if (reviewedCollection) setShownCollection(reviewedCollection);
  }
  const showClientPolicies = shownCollection === "client";
  const showVendorPolicies = shownCollection === "vendor";

  async function handleDownloadDocumentById(
    documentId?: Guid,
    event?: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ): Promise<void> {
    event?.stopPropagation();
    if (!documentId) return;

    try {
      setIsDownloadingFile(documentId);
      const policyTemplate = await downloadDocument({ documentId: documentId.value }).unwrap();
      await downloadFile(policyTemplate);
    } catch (err) {
      console.error(err);
      showErrorSnackbar(err, { doing: "downloading the policy template", operation: "proposal.policies.download" });
    } finally {
      setIsDownloadingFile(null);
    }
  }

  function handleDocumentRedlineChanged(
    newRedline: FieldRedlineArray<WorkDocument>,
    traversalFieldOverride?: ProposalField | null
  ) {
    let newProposalRedline: ProposalRedline;
    if (newRedline.field.isEqualTo(ProposalField.ClientPolicies)) {
      newProposalRedline = proposalRedline.updateClientPoliciesRedline(newRedline);
    } else if (newRedline.field.isEqualTo(ProposalField.VendorPolicies)) {
      newProposalRedline = proposalRedline.updateVendorPoliciesRedline(newRedline);
    } else {
      throw new Error("Unable to determine which policy redline to update.");
    }

    onProposalRedlineChange(newProposalRedline, traversalFieldOverride);
  }

  function handleDocumentSelectionChanged(selectedDocs: Document[], deselectedDocs: Document[]) {
    if (!selectedDocs.length && !deselectedDocs.length) return;
    // TODO: Is a full clone necessary here?
    let updatedProposalRedline = proposalRedline;

    for (const selectedDoc of selectedDocs) {
      const workDocument = WorkDocument.fromDocument(
        selectedDoc,
        session.context?.viewingAsVendor ? WorkDocumentType.VendorPolicy : WorkDocumentType.ClientPolicy
      );
      if (
        Masked.isMasked(updatedProposalRedline.clientPolicies) ||
        Masked.isMasked(updatedProposalRedline.vendorPolicies)
      ) {
        throw new Error("You do not have permission to modify contracts.");
      }
      if (
        !session.context?.viewingAsVendor &&
        !updatedProposalRedline.clientPolicies.redlines.some((document) =>
          document.currentEntry?.is(workDocument)
        )
      ) {
        updatedProposalRedline = updatedProposalRedline.updateClientPoliciesRedline(
          updatedProposalRedline.clientPolicies.addEntry(workDocument)
        );
      }

      if (
        Masked.isMasked(updatedProposalRedline.clientPolicies) ||
        Masked.isMasked(updatedProposalRedline.vendorPolicies)
      ) {
        throw new Error("You do not have permission to modify contracts.");
      }
      if (
        session.context?.viewingAsVendor &&
        !updatedProposalRedline.vendorPolicies.redlines.some((document) =>
          document.currentEntry?.is(workDocument)
        )
      ) {
        updatedProposalRedline = updatedProposalRedline.updateVendorPoliciesRedline(
          updatedProposalRedline.vendorPolicies.addEntry(workDocument)
        );
      }
    }

    for (const deselectedDoc of deselectedDocs) {
      if (
        Masked.isMasked(updatedProposalRedline.clientPolicies) ||
        Masked.isMasked(updatedProposalRedline.vendorPolicies)
      ) {
        throw new Error("You do not have permission to modify contracts.");
      }
      if (
        !session.context?.viewingAsVendor &&
        updatedProposalRedline.clientPolicies.redlines.some((document) =>
          document.currentEntry?.id.isEqualTo(deselectedDoc.id)
        )
      ) {
        const redlineId = updatedProposalRedline.clientPolicies.redlines.find(
          (documentRedline) =>
            documentRedline.currentEntry?.id?.isEqualTo(deselectedDoc.id) ||
            (!documentRedline.currentEntry && documentRedline.revisedEntry?.id?.isEqualTo(deselectedDoc.id))
        )?.field.id;

        if (!redlineId) {
          console.error("Unable to determine which policy redline to update.");
          continue;
        }

        updatedProposalRedline = updatedProposalRedline.updateClientPoliciesRedline(
          updatedProposalRedline.clientPolicies.removeEntryByFieldId(redlineId)
        );
      }

      if (
        Masked.isMasked(updatedProposalRedline.clientPolicies) ||
        Masked.isMasked(updatedProposalRedline.vendorPolicies)
      ) {
        throw new Error("You do not have permission to modify contracts.");
      }
      if (
        session.context?.viewingAsVendor &&
        updatedProposalRedline.vendorPolicies.redlines.some((document) =>
          document.currentEntry?.id.isEqualTo(deselectedDoc.id)
        )
      ) {
        const redlineId = updatedProposalRedline.vendorPolicies.redlines.find(
          (documentRedline) =>
            documentRedline.currentEntry?.id?.isEqualTo(deselectedDoc.id) ||
            (!documentRedline.currentEntry && documentRedline.revisedEntry?.id?.isEqualTo(deselectedDoc.id))
        )?.field.id;
        if (!redlineId) {
          console.error("Unable to determine which policy redline to update.");
          continue;
        }
        updatedProposalRedline = updatedProposalRedline.updateVendorPoliciesRedline(
          updatedProposalRedline.vendorPolicies.removeEntryByFieldId(redlineId)
        );
      }
    }

    let updatedDocumentRedline = session.context?.viewingAsVendor
      ? updatedProposalRedline.vendorPolicies
      : updatedProposalRedline.clientPolicies;

    if (Masked.isMasked(updatedDocumentRedline)) {
      throw new Error("You do not have permission to modify contracts.");
    }
    if (session.context?.viewingAsVendor) {
      updatedProposalRedline = proposalRedline.updateVendorPoliciesRedline(updatedDocumentRedline);
    } else {
      updatedProposalRedline = proposalRedline.updateClientPoliciesRedline(updatedDocumentRedline);
    }

    onProposalRedlineChange(updatedProposalRedline, null);
  }

  async function handleBeginCreateDocumentFromTemplate(templateId: Guid | undefined | null) {
    if (!templateId) return;
    setDocumentTypeToUpload(
      session.context?.viewingAsVendor ? WorkDocumentType.VendorPolicy : WorkDocumentType.ClientPolicy
    );
    setCreatingDocumentFromTemplate(templateId);
    await handleDownloadDocumentById(templateId);
  }

  /** How many of the reader's side's policies are on the proposal, for the collapsed rail's count. */
  function selectedPolicyCount(): number {
    const documentsRedline = session.context?.viewingAsVendor
      ? proposalRedline.vendorPolicies
      : proposalRedline.clientPolicies;
    return Masked.isMasked(documentsRedline) ? 0 : documentsRedline.currentIds.length;
  }

  function renderDocumentSelector() {
    const documentsRedline = session.context?.viewingAsVendor
      ? proposalRedline.vendorPolicies
      : proposalRedline.clientPolicies;
    if (Masked.isMasked(documentsRedline)) {
      throw new Error("You do not have permission to view contracts.");
    }
    let selectedTemplateIds: Guid[] = [];
    for (const redline of documentsRedline.redlines) {
      if (redline.currentEntry?.templateIds) {
        selectedTemplateIds = selectedTemplateIds.concat(redline.currentEntry.templateIds);
      }
    }
    return (
      <DocumentSelector
        compact={selectorIsCompact}
        documentType="policy"
        proposalId={proposalId}
        selectedDocumentIds={documentsRedline.currentIds}
        selectedTemplateIds={selectedTemplateIds}
        onDocumentSelectionChanged={handleDocumentSelectionChanged}
        onCreateDocumentFromTemplate={handleBeginCreateDocumentFromTemplate}
      />
    );
  }

  async function handleBeginReplaceDocument(documentId: Guid | undefined | null, documentType: WorkDocumentType) {
    if (!documentId) return;

    setDocumentTypeToUpload(documentType);
    setDocumentIdToReplace(documentId);
    await handleDownloadDocumentById(documentId);
  }

  function handlePolicyDocumentAdded(document: Document | UnavailableDocument) {
    if (document instanceof UnavailableDocument) {
      throw new TypeError("Document must be available");
    }

    let updatedProposalRedline = proposalRedline;

    if (
      Masked.isMasked(proposalRedline.vendorPolicies) ||
      Masked.isMasked(proposalRedline.clientPolicies) ||
      Masked.isMasked(updatedProposalRedline.vendorPolicies) ||
      Masked.isMasked(updatedProposalRedline.clientPolicies)
    ) {
      throw new Error("You do not have permission to modify contracts.");
    }
    const documentType =
      documentTypeToUpload ??
      (session.context?.viewingAsVendor ? WorkDocumentType.VendorPolicy : WorkDocumentType.ClientPolicy);
    const documentsRedline =
      documentType === WorkDocumentType.VendorPolicy ? proposalRedline.vendorPolicies : proposalRedline.clientPolicies;
    const workDocument = WorkDocument.fromDocument(
      document,
      documentType,
      documentIdToReplace ?? undefined,
      getReplacementDocumentTemplateIds(documentsRedline, documentIdToReplace)
    );
    if (documentIdToReplace && documentTypeToUpload === WorkDocumentType.VendorPolicy) {
      updatedProposalRedline = proposalRedline.updateVendorPoliciesRedline(
        proposalRedline.vendorPolicies.replaceEntryById(documentIdToReplace, workDocument)
      );
    } else if (documentIdToReplace && documentTypeToUpload === WorkDocumentType.ClientPolicy) {
      updatedProposalRedline = proposalRedline.updateClientPoliciesRedline(
        proposalRedline.clientPolicies.replaceEntryById(documentIdToReplace, workDocument)
      );
    } else if (documentTypeToUpload === WorkDocumentType.VendorPolicy) {
      updatedProposalRedline = proposalRedline.updateVendorPoliciesRedline(
        proposalRedline.vendorPolicies.addEntry(workDocument)
      );
    } else if (documentTypeToUpload === WorkDocumentType.ClientPolicy) {
      updatedProposalRedline = proposalRedline.updateClientPoliciesRedline(
        proposalRedline.clientPolicies.addEntry(workDocument)
      );
    }

    onProposalRedlineChange(updatedProposalRedline, null);
    setDocumentIdToReplace(null);
    setCreatingDocumentFromTemplate(null);
  }

  if (Masked.isMasked(proposalRedline.clientPolicies) || Masked.isMasked(proposalRedline.vendorPolicies))
    return maskedProposalFieldNotice(MaskedProposalField.Policies);
  return (
    <>
      <ListContainer container direction="row">
        {!disableEditing && (
          <>
            <ResizableTemplatePanel
              minimized={isDrawerOpen}
              selectedCount={selectedPolicyCount()}
            >
              {renderDocumentSelector()}
            </ResizableTemplatePanel>
            <SelectorDivider>
              <Divider orientation="vertical" />
            </SelectorDivider>
          </>
        )}
        <SelectedDocsPanel>
          {(issues?.length ?? 0) > 0 && (
            <HeadControls>
              <ProposalIssueList
                issues={issues ?? []}
                activeReviewField={activeReviewField}
                onIssueSelected={(issue) => onTraverseToNewField?.(issue.field)}
              />
            </HeadControls>
          )}
          <PolicyCollectionTabs
            aria-label="Policies"
            value={shownCollection}
            variant={isPhone ? "fullWidth" : "standard"}
            onChange={(_event, value: PolicyCollection) => setShownCollection(value)}
          >
            <Tab
              value="client"
              label={
                <PolicyCollectionTabLabel
                  title="Client Policies"
                  about={POLICY_COLLECTION_ABOUT.client}
                  count={proposalRedline.clientPolicies.redlines.length}
                  issues={props.issues}
                  envelope={props.unreadEnvelope}
                  currentUserId={props.currentUserId}
                  fieldFilter={isClientPolicyField}
                />
              }
            />
            <Tab
              value="vendor"
              label={
                <PolicyCollectionTabLabel
                  title="Vendor Policies"
                  about={POLICY_COLLECTION_ABOUT.vendor}
                  count={proposalRedline.vendorPolicies.redlines.length}
                  issues={props.issues}
                  envelope={props.unreadEnvelope}
                  currentUserId={props.currentUserId}
                  fieldFilter={isVendorPolicyField}
                />
              }
            />
          </PolicyCollectionTabs>
          <DocumentGrid>
            {showClientPolicies && (
            <SelectedDocsList
              showHeading={false}
              workDocumentType={WorkDocumentType.ClientPolicy}
              proposalId={proposalId}
              documentsRedline={proposalRedline.clientPolicies}
              accountType={AccountType.Client}
              negotiationId={negotiationId}
              viewerScope={viewerScope}
              downloadingFileId={isDownloadingFile}
              disableEditing={disableEditing}
              fieldLockTooltip={fieldLockTooltip}
              activeReviewField={activeReviewField}
              onDownload={handleDownloadDocumentById}
              onDocumentRedlineChange={(newRedline, traversalFieldOverride) => {
                try {
                  handleDocumentRedlineChanged(newRedline, traversalFieldOverride);
                } catch (error) {
                  console.error(error);
                  showErrorSnackbar(error, { doing: "updating the policy", operation: "proposal.policies.update" });
                }
              }}
              onBeginReplaceDocument={(docId) => handleBeginReplaceDocument(docId, WorkDocumentType.ClientPolicy)}
              onDocumentClicked={(documentId: Guid) => {
                onTraverseToNewField?.(ProposalField.ClientPolicy(documentId));
              }}
              onTraverseToNewField={onTraverseToNewField}
            />
            )}
            {showVendorPolicies && (
            <SelectedDocsList
              showHeading={false}
              workDocumentType={WorkDocumentType.VendorPolicy}
              proposalId={proposalId}
              documentsRedline={proposalRedline.vendorPolicies}
              accountType={AccountType.Vendor}
              negotiationId={negotiationId}
              viewerScope={viewerScope}
              downloadingFileId={isDownloadingFile}
              disableEditing={disableEditing}
              fieldLockTooltip={fieldLockTooltip}
              activeReviewField={activeReviewField}
              onDownload={handleDownloadDocumentById}
              onDocumentRedlineChange={(newRedline, traversalFieldOverride) => {
                try {
                  handleDocumentRedlineChanged(newRedline, traversalFieldOverride);
                } catch (error) {
                  console.error(error);
                  showErrorSnackbar(error, { doing: "updating the policy", operation: "proposal.policies.update" });
                }
              }}
              onBeginReplaceDocument={(docId) => handleBeginReplaceDocument(docId, WorkDocumentType.VendorPolicy)}
              onDocumentClicked={(documentId: Guid) => {
                onTraverseToNewField?.(ProposalField.VendorPolicy(documentId));
              }}
              onTraverseToNewField={onTraverseToNewField}
            />
            )}
          </DocumentGrid>
        </SelectedDocsPanel>
      </ListContainer>
      {documentTypeToUpload && (creatingDocumentFromTemplate || documentIdToReplace) && (
        <Uploader
          documentType={documentTypeToUpload}
          proposalId={proposalId}
          templateId={creatingDocumentFromTemplate ?? undefined}
          templateIds={
            creatingDocumentFromTemplate
              ? undefined
              : getReplacementDocumentTemplateIds(
                  documentTypeToUpload === WorkDocumentType.VendorPolicy
                    ? proposalRedline.vendorPolicies
                    : proposalRedline.clientPolicies,
                  documentIdToReplace
                )
          }
          onDocumentUploaded={handlePolicyDocumentAdded}
          onClose={() => {
            setCreatingDocumentFromTemplate(null);
            setDocumentIdToReplace(null);
          }}
        />
      )}
    </>
  );
};

export default PoliciesTab;
